import React, { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { ChevronRight, ChevronDown, Search, Database, FileText, UserRound, Wrench } from 'lucide-react';

const TOOL_META = {
  search_embeddings: { label: 'Поиск по базе знаний', Icon: Search, color: '#34d399' },
  list_analysis_types: { label: 'Типы анализа', Icon: Database, color: '#60a5fa' },
  get_analysis_items: { label: 'Элементы анализа', Icon: Database, color: '#60a5fa' },
  get_analysis_item: { label: 'Элемент анализа', Icon: Database, color: '#60a5fa' },
  get_character: { label: 'Данные персонажа', Icon: UserRound, color: '#a78bfa' },
  list_characters: { label: 'Список персонажей', Icon: UserRound, color: '#a78bfa' },
  write_doc: { label: 'Создание документа', Icon: FileText, color: '#fbbf24' },
  get_doc: { label: 'Чтение документа', Icon: FileText, color: '#fbbf24' },
  list_docs: { label: 'Список документов', Icon: FileText, color: '#fbbf24' },
  delete_doc: { label: 'Удаление документа', Icon: FileText, color: '#ef4444' },
  move_doc: { label: 'Перемещение документа', Icon: FileText, color: '#fbbf24' }
};

function ToolCallEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const meta = TOOL_META[entry.tool] || { label: entry.tool, Icon: Wrench, color: '#a1a1aa' };
  const Icon = meta.Icon;
  const hasDetails = entry.details && entry.details.length > 0;

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => hasDetails && setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.4,
          px: 0.75,
          borderRadius: '6px',
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': hasDetails ? { bgcolor: 'rgba(255,255,255,0.04)' } : {},
          transition: 'background 0.15s'
        }}
      >
        {hasDetails ? (
          open ? <ChevronDown size={12} style={{ color: '#71717a', flexShrink: 0 }} /> : <ChevronRight size={12} style={{ color: '#71717a', flexShrink: 0 }} />
        ) : (
          <Box sx={{ width: 12, flexShrink: 0 }} />
        )}
        <Icon size={13} style={{ color: meta.color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.72rem', lineHeight: 1.3 }}>
          <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
          {entry.args?.query && (
            <span style={{ color: '#71717a' }}>{' \u2014 '}&laquo;{entry.args.query}&raquo;</span>
          )}
          {entry.summary && (
            <span style={{ color: entry.success === false ? '#ef4444' : '#a1a1aa' }}>
              {' \u2014 '}{entry.summary}
            </span>
          )}
        </Typography>
      </Box>

      {hasDetails && (
        <Collapse in={open}>
          <Box sx={{ pl: 4, pr: 1, pb: 0.5 }}>
            {entry.details.map((d, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.75,
                  py: 0.2
                }}
              >
                {d.similarity != null && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.68rem',
                      color: Number(d.similarity) >= 0.5 ? '#34d399' : Number(d.similarity) >= 0.35 ? '#fbbf24' : '#ef4444',
                      fontWeight: 600,
                      flexShrink: 0,
                      minWidth: 38,
                      textAlign: 'right'
                    }}
                  >
                    {d.similarity}
                  </Typography>
                )}
                {d.type && (
                  <Typography variant="caption" sx={{ color: '#71717a', fontSize: '0.68rem', flexShrink: 0 }}>
                    [{d.type}]
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: '#a1a1aa', fontSize: '0.68rem', lineHeight: 1.3 }} noWrap>
                  {d.text || d.sourceId || ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default function NexusToolCallLog({ toolCalls }) {
  const [expanded, setExpanded] = useState(false);

  if (!toolCalls || toolCalls.length === 0) return null;

  const totalLabel = toolCalls.length === 1
    ? '1 инструмент'
    : `${toolCalls.length} инструментов`;

  const toolNames = [...new Set(toolCalls.map((tc) => (TOOL_META[tc.tool]?.label || tc.tool)))];
  const briefSummary = toolNames.join(', ');

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid rgba(139,92,246,0.15)',
        bgcolor: 'rgba(139,92,246,0.04)',
        mb: 0.75,
        overflow: 'hidden'
      }}
    >
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.6,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(139,92,246,0.08)' },
          transition: 'background 0.15s'
        }}
      >
        {expanded
          ? <ChevronDown size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />
          : <ChevronRight size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />
        }
        <Wrench size={13} style={{ color: '#a78bfa', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, fontSize: '0.73rem' }}>
          {totalLabel}
        </Typography>
        {!expanded && (
          <Typography variant="caption" sx={{ color: '#71717a', fontSize: '0.7rem', ml: 0.5 }} noWrap>
            {briefSummary}
          </Typography>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 0.5, pb: 0.75 }}>
          {toolCalls.map((tc, i) => (
            <ToolCallEntry key={i} entry={tc} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
